import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import SuperAdminLayout from '../SuperAdminLayout';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { parseSuperAdminError } from '../../../utils/superAdminErrors';
import { SUPPORTED_LOCALES as ALL_LOCALES, LOCALE_META } from '../../../i18n/config';

const SUPPORTED_LOCALES: { value: string; label: string }[] = ALL_LOCALES.map(code => ({
    value: code,
    label: `${LOCALE_META[code].native} (${code})`,
}));

const CreateCompanyPage: React.FC = () => {
    const history = useHistory();
    const [currentEmail, setCurrentEmail] = useState<string | null>(null);

    // Company fields
    const [name, setName] = useState('');
    const [defaultLocale, setDefaultLocale] = useState('sq');
    const [activeLocales, setActiveLocales] = useState<string[]>(['sq', 'en']);
    const [maxUsers, setMaxUsers] = useState('');

    // Admin fields
    const [adminUsername, setAdminUsername] = useState('');
    const [adminFirstname, setAdminFirstname] = useState('');
    const [adminLastname, setAdminLastname] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [banner, setBanner] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        saRequest('GET', 'auth/me')
            .then(res => setCurrentEmail(res.data.super_admin?.email ?? null))
            .catch(() => {});
    }, []);

    const toggleActiveLocale = (loc: string) => {
        setActiveLocales(curr =>
            curr.includes(loc) ? curr.filter(l => l !== loc) : [...curr, loc],
        );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setBanner(null);
        setFieldErrors({});

        const payload: Record<string, any> = {
            name: name.trim(),
            default_locale: defaultLocale,
            active_locales: activeLocales,
            // null = unlimited. Empty input string → null; numeric → parsed int.
            max_users: maxUsers.trim() === '' ? null : Number(maxUsers.trim()),
            admin: {
                username: adminUsername.trim(),
                firstname: adminFirstname.trim(),
                lastname: adminLastname.trim(),
                email: adminEmail.trim().toLowerCase(),
                password: adminPassword,
            },
        };

        try {
            const res = await saRequest<{ company: { id: number } }>('POST', 'companies', payload);
            const newId = res.data.company?.id;
            if (newId) {
                history.replace(`/mika/ops/companies/${newId}`);
            } else {
                history.replace('/mika/ops/companies');
            }
        } catch (err: any) {
            if (err?.response?.status === 401) {
                history.replace('/mika/ops/login');
                return;
            }
            const p = parseSuperAdminError(err);
            setBanner(p.banner);
            setFieldErrors(p.fields);
        } finally {
            setSubmitting(false);
        }
    };

    // Backend returns field errors keyed as "admin.email", "admin.password", etc.
    // Read both flat + nested forms so display is tolerant to either shape.
    const err = (flat: string, nested?: string): string | undefined =>
        fieldErrors[flat] ?? (nested ? fieldErrors[nested] : undefined);

    return (
        <SuperAdminLayout currentEmail={currentEmail}>
            <div style={{ marginBottom: 'var(--sa-s5)' }}>
                <Link
                    to="/mika/ops/companies"
                    style={{
                        color: 'var(--sa-text-tertiary)',
                        fontSize: 'var(--sa-fs-sm)',
                        textDecoration: 'none',
                        display: 'inline-block',
                        marginBottom: 'var(--sa-s3)',
                    }}
                >
                    ← Back to companies
                </Link>
                <h1 style={{ margin: 0, fontSize: 'var(--sa-fs-heading)', fontWeight: 600, letterSpacing: '-0.02em' }}>
                    Create a new tenant
                </h1>
                <div style={{ color: 'var(--sa-text-tertiary)', fontSize: 'var(--sa-fs-sm)', marginTop: 4 }}>
                    Sets up the company row, seeds its default data, and grants the initial admin <span className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>superAdmin</span> + <span className="sa-mono" style={{ color: 'var(--sa-text-secondary)' }}>fullPrivileges</span>.
                </div>
            </div>

            <div className="sa-card" style={{ maxWidth: 640 }}>
                {banner && <div className="sa-error-banner">{banner}</div>}

                <form onSubmit={handleSubmit} noValidate>
                    <div className="sa-section-label" style={{ margin: '0 0 var(--sa-s3)' }}>Company</div>

                    <div className="sa-field">
                        <label>Company name</label>
                        <input type="text" value={name} onChange={e => setName(e.target.value)} disabled={submitting} required />
                        {err('name') && <div className="sa-field-error">{err('name')}</div>}
                    </div>

                    <div className="sa-field">
                        <label>Default locale</label>
                        <select value={defaultLocale} onChange={e => setDefaultLocale(e.target.value)} disabled={submitting}>
                            {SUPPORTED_LOCALES.map(l => (
                                <option key={l.value} value={l.value}>{l.label}</option>
                            ))}
                        </select>
                        {err('default_locale') && <div className="sa-field-error">{err('default_locale')}</div>}
                    </div>

                    <div className="sa-field">
                        <label>Active locales</label>
                        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                            {SUPPORTED_LOCALES.map(l => (
                                <label key={l.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={activeLocales.includes(l.value)}
                                        onChange={() => toggleActiveLocale(l.value)}
                                        disabled={submitting}
                                    />
                                    <span>{l.label}</span>
                                </label>
                            ))}
                        </div>
                        {err('active_locales') && <div className="sa-field-error">{err('active_locales')}</div>}
                    </div>

                    <div className="sa-field">
                        <label>User seat limit (optional)</label>
                        <input
                            type="number"
                            min={1}
                            max={100000}
                            value={maxUsers}
                            onChange={e => setMaxUsers(e.target.value)}
                            disabled={submitting}
                            placeholder="Leave blank for unlimited"
                        />
                        {err('max_users') && <div className="sa-field-error">{err('max_users')}</div>}
                        <div className="sa-field-help">
                            When reached, the tenant's "Create user" action is blocked with a toast.
                        </div>
                    </div>

                    <hr className="sa-hairline" style={{ margin: 'var(--sa-s5) 0 var(--sa-s4)' }} />
                    <div className="sa-section-label" style={{ margin: '0 0 var(--sa-s3)' }}>Initial admin user</div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div className="sa-field" style={{ marginBottom: 0 }}>
                            <label>First name</label>
                            <input type="text" value={adminFirstname} onChange={e => setAdminFirstname(e.target.value)} disabled={submitting} required />
                            {err('admin.firstname') && <div className="sa-field-error">{err('admin.firstname')}</div>}
                        </div>
                        <div className="sa-field" style={{ marginBottom: 0 }}>
                            <label>Last name</label>
                            <input type="text" value={adminLastname} onChange={e => setAdminLastname(e.target.value)} disabled={submitting} required />
                            {err('admin.lastname') && <div className="sa-field-error">{err('admin.lastname')}</div>}
                        </div>
                    </div>

                    <div className="sa-field" style={{ marginTop: 12 }}>
                        <label>Username</label>
                        <input type="text" value={adminUsername} onChange={e => setAdminUsername(e.target.value)} disabled={submitting} required autoComplete="off" />
                        {err('admin.username') && <div className="sa-field-error">{err('admin.username')}</div>}
                    </div>

                    <div className="sa-field">
                        <label>Email</label>
                        <input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} disabled={submitting} required autoComplete="off" />
                        {err('admin.email') && <div className="sa-field-error">{err('admin.email')}</div>}
                    </div>

                    <div className="sa-field">
                        <label>Initial password (min 8 chars)</label>
                        <input type="password" value={adminPassword} onChange={e => setAdminPassword(e.target.value)} disabled={submitting} required autoComplete="new-password" />
                        {err('admin.password') && <div className="sa-field-error">{err('admin.password')}</div>}
                        <div className="sa-field-help">
                            Share this with the admin via a secure channel — they can change it after first login.
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 'var(--sa-s2)', marginTop: 'var(--sa-s5)' }}>
                        <button
                            type="submit"
                            className="sa-btn sa-btn-primary"
                            disabled={submitting || !name || !adminEmail || !adminPassword || activeLocales.length === 0}
                        >
                            {submitting && <span className="sa-btn-spinner" />}
                            {submitting ? 'Creating…' : 'Create company'}
                        </button>
                        <Link to="/mika/ops/companies" className="sa-btn sa-btn-secondary" style={{ textDecoration: 'none' }}>
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </SuperAdminLayout>
    );
};

export default CreateCompanyPage;
